import * as React from 'react'

import type { OntologyAgentEvent } from '@atbash-labs/ontology-agent-wire'

import { getOntologyServiceBaseUrl } from '../config'
import { consumeSSE } from './sse-client'
import {
  emptyIngestCotReduceState,
  reduceIngestCotEvent,
  type IngestCotByPill,
  type IngestCotCommittedTurn,
  type IngestCotPillTail
} from './ingest-cot-stream-reduce'

export type {
  IngestCotByPill,
  IngestCotCommittedTurn,
  IngestCotPillKey,
  IngestCotPillTail
} from './ingest-cot-stream-reduce'
export { ingestCotPillKey, INGEST_LEAD_PILL_KEY } from './ingest-cot-stream-reduce'

export interface IngestCotStream {
  /**
   * Live CoT tails sharded by pill key (`lead` or `dispatch:{id}`). Phase 3
   * renders one section per pill; until then consumers can read `liveText` /
   * `committed` for the lead-only convenience fields below.
   */
  byPill: IngestCotByPill
  /** Lead pill's in-flight turn text ('' between turns). */
  liveText: string
  /**
   * Lead turns that finished streaming but may not be in the fetched
   * transcript yet. The consumer renders these (deduped against the
   * transcript) so committed text doesn't blink out of the thread until the
   * transcript refetch lands.
   */
  committed: readonly IngestCotCommittedTurn[]
}

const EMPTY_PILL: IngestCotPillTail = {
  liveText: '',
  committed: [],
  subagentKind: 'ingest_agent'
}

/**
 * Live chain-of-thought tail for an in-flight unstructured-ingest run.
 *
 * The run executes headless in the agent-executor poller and publishes its
 * events over Redis; the api-service fans them out on the job-scoped SSE
 * endpoint (`POST /ingest/jobs/:jobId/stream`). This hook subscribes while the
 * run is active and accumulates between-tool progress-note deltas into
 * per-pill live tails (lead + each dispatched child).
 *
 * The endpoint replays a Redis buffer before going live, so a mid-run page
 * load catches up to the in-flight turn (replayed commits land in `committed`
 * and dedupe away against the already-loaded transcript). Best-effort: a
 * dropped stream just stops the live tail.
 */
export function useIngestCotStream(jobId: string, active: boolean): IngestCotStream {
  const [byPill, setByPill] = React.useState<IngestCotByPill>({})

  React.useEffect(() => {
    if (!jobId || !active) {
      setByPill({})
      return
    }

    const controller = new AbortController()
    let reduceState = emptyIngestCotReduceState()

    void consumeSSE<OntologyAgentEvent>({
      url: `${getOntologyServiceBaseUrl()}/ingest/jobs/${encodeURIComponent(jobId)}/stream`,
      body: {},
      signal: controller.signal,
      onEvent: (event) => {
        const next = reduceIngestCotEvent(reduceState, event)
        if (next === reduceState) return
        reduceState = next
        setByPill(next.byPill)
      }
    }).catch(() => undefined)

    return () => {
      controller.abort()
      setByPill({})
    }
  }, [jobId, active])

  const lead = byPill.lead ?? EMPTY_PILL
  return {
    byPill,
    liveText: lead.liveText,
    committed: lead.committed
  }
}
